import { useEffect } from 'react';

import { useQueryClient } from '@zg-rentals/react-query5';
import {
  useRentalManagerUserPropertyDetailsWebApiHandler_details,
  useRentalManagerUserPropertySummaryWebApiHandler_listing,
} from '@zg-rentals/ts-rental-manager-api-beta-client-react-query';

type UseSyndicationUpdatesOptions = {
  listingAlias: string;
  enabled?: boolean;
};

/**
 * Listens for real-time syndication status updates via WebSocket and invalidates
 * the listing details cache to trigger a refetch.
 *
 * @param listingAlias - The listing to listen for updates on
 * @param enabled - Whether to listen for updates (default: true)
 */
export const useSyndicationUpdates = ({ listingAlias, enabled = true }: UseSyndicationUpdatesOptions) => {
  const queryClient = useQueryClient();

  useEffect(() => {
    if (!enabled || !listingAlias || typeof window === 'undefined') return;

    if (!window.rentalManagerApiWebSocket) return;

    const handleMessage = (event: MessageEvent) => {
      let data;
      try {
        data = JSON.parse(event.data);
      } catch {
        return;
      }

      // Only handle listing syndication status updates
      if (data.name !== 'listingSyndicationStatus') return;

      const { payload } = data;

      if (payload.listingAlias !== listingAlias) return;

      // Invalidate both the listing summary and property details so the UI reflects
      // the updated syndication status without a full page reload.
      queryClient.invalidateQueries({
        queryKey: useRentalManagerUserPropertyDetailsWebApiHandler_details.getKey({ listingAlias }),
      });
      queryClient.invalidateQueries({
        queryKey: useRentalManagerUserPropertySummaryWebApiHandler_listing.getKey({ listingAlias }),
      });
    };

    window.rentalManagerApiWebSocket.addEventListener('message', handleMessage);

    return () => {
      window.rentalManagerApiWebSocket?.removeEventListener('message', handleMessage);
    };
  }, [listingAlias, enabled, queryClient]);
};
